import { ApprovalRequiredAlert } from "@/features/reserve/components/Reserve/views/ApprovalRequiredAlert";
import { UserHealthRestrictedAlert } from "@/features/reserve/components/Reserve/views/UserHealthRestrictedAlert";
import React from "react";
import { ReserveProps } from "@/features/reserve/components/Reserve/types";
import { ReserveFormDates } from "@/features/reserve/components/Reserve/controls/ReserveFormDates";
import { ReserveFormStartTime } from "@/features/reserve/components/Reserve/controls/ReserveFormStartTime";
import { ReserveFormBuffer } from "@/features/reserve/components/Reserve/controls/ReserveFormBuffer";
import { ReserveFormDuration } from "@/features/reserve/components/Reserve/controls/ReserveFormDuration";
import { ReserveFormResources } from "@/features/reserve/components/Reserve/controls/ReserveFormResources";
import { ReserveFormRestrictionAlerts } from "@/features/reserve/components/Reserve/views/ReserveFormRestrictionAlerts";
import { ReserveFormOpenHourRuleAlerts } from "@/features/reserve/components/Reserve/views/ReserveFormOpenHourRuleAlerts";
import { ReserveFormRecurring } from "@/features/reserve/components/Reserve/views/ReserveFormRecurring";
import { ReserveFormParking } from "@/features/reserve/components/Reserve/views/ReserveFormParking";
import { ReserveFormErrors } from "@/features/reserve/components/Reserve/views/ReserveFormErrors";
import { ReserveFormFloor } from "@/features/reserve/components/Reserve/views/ReserveFormFloor";
import { ReserveFormTimeZoneMismatchAlert } from "@/features/reserve/components/Reserve/views/ReserveFormTimeZoneMismatchAlert";
import { ReserveFormActions } from "@/features/reserve/components/Reserve/views/ReserveFormActions";
import { ReserveFormForm } from "@/features/reserve/components/Reserve/ReserveFormForm";
import { ReserveFormOtherUserReservation } from "@/features/reserve/components/Reserve/views/ReserveFormOtherUserReservation";
import { ReserveFormMeetingRoomUsers } from "@/features/reserve/components/Reserve/views/ReserveFormMeetingRoomUsers";
import { ReserveFormMeetingRoomTitle } from "@/features/reserve/components/Reserve/views/ReserveFormMeetingRoomTitle";
import { ReserveFormOnlineMeeting } from "@/features/reserve/components/Reserve/controls/ReserveFormOnlineMeeting";
import { ReserveFormVisibility } from "@/features/reserve/components/Reserve/controls/ReserveFormVisibility";
import { ReserveFormWPRequests } from "@/features/reserve/components/Reserve/controls/ReserveFormWPRequests";
import { cn } from "@gettactic/helpers";
import { Label } from "@cmp-src/primitives/label";
import { Separator } from "@cmp-src/primitives/separator";
import { ReserveFormContext } from "@cmp-src/reserve/ReverseFormContext";

const classes = {
  // One vertical rhythm for the whole form: sections, then label→field,
  // then rows inside a section. Fields never carry margins of their own.
  Form: "flex flex-col gap-5",
  Section: "flex flex-col gap-1.5",
  SectionRows: "flex flex-col gap-2",
  // Each time gets a fixed width that fits the longest value ("12:45pm"), so
  // it is never cut off and does not jump as the value changes; the date,
  // which has room to spare, takes whatever is left.
  DateTimeRow: "grid grid-cols-[minmax(0,1fr)_7.5rem_7.5rem] gap-2",
  DateCell: "min-w-0",
  TimeCell: "min-w-0"
};

export const Reserve: React.FC<ReserveProps> = ({
  onClose,
  showActions = true,
  isCompact = false
}) => {
  return (
    <ReserveFormContext.Consumer>
      {(state) => {
        if (!state) return null;
        const { form, submitReservation } = state;
        const isRoom = state.resourceType === "meeting_room";
        return (
          <ReserveFormForm onSubmit={form.handleSubmit(submitReservation)}>
            <div className={classes.Form}>
              {/* Alerts live in the same column as the fields so the gap
                  between an alert and the first field matches the rest. */}
              {onClose ? (
                <ReserveFormTimeZoneMismatchAlert onClose={onClose} />
              ) : null}
              <ReserveFormRestrictionAlerts />
              <ReserveFormOpenHourRuleAlerts />
              <UserHealthRestrictedAlert className="max-w-xl" />
              <ApprovalRequiredAlert className="max-w-xl" />

              <ReserveFormOtherUserReservation />

              <ReserveFormMeetingRoomTitle />

              <div className={classes.Section}>
                <Label htmlFor="dates">Date & time</Label>
                <div className={classes.SectionRows}>
                  <div className={classes.DateTimeRow}>
                    <div className={classes.DateCell}>
                      <ReserveFormDates />
                    </div>
                    <div className={classes.TimeCell}>
                      <ReserveFormStartTime />
                    </div>
                    <div className={classes.TimeCell}>
                      <ReserveFormDuration />
                    </div>
                  </div>
                  <ReserveFormRecurring isCompact={isCompact} />
                </div>
              </div>

              <ReserveFormBuffer />

              {/* Kept mounted while hidden: the resource field registers its
                  validation rule from inside the list. */}
              <div className={cn(classes.Section, isCompact && "hidden")}>
                <Label htmlFor="floor">Location & workspace</Label>
                <div className={classes.SectionRows}>
                  <ReserveFormFloor />
                  <div className="relative text-left">
                    <ReserveFormResources />
                  </div>
                </div>
              </div>

              <ReserveFormParking />

              {isRoom && (
                <div className={classes.Section}>
                  <Label htmlFor="guests">Guests</Label>
                  <ReserveFormMeetingRoomUsers
                    defaultUsers={state.defaultValues.users}
                  />
                </div>
              )}

              {isRoom && <Separator className="bg-neutral-100" />}

              {isRoom && (
                <ReserveFormOnlineMeeting
                  isEdit={false}
                  isMinimalistic={true}
                />
              )}

              {isRoom && <ReserveFormVisibility />}

              <ReserveFormWPRequests
                workplaceRequestItems={state.workplaceRequestItems}
              />

              <ReserveFormErrors />

              {showActions && <ReserveFormActions />}
            </div>
          </ReserveFormForm>
        );
      }}
    </ReserveFormContext.Consumer>
  );
};
